import { create } from 'zustand'

type SelectionState = {
  selectedFolderIds: string[]
  selectedDiagramIds: string[]
  toggleFolder: (id: string) => void
  toggleDiagram: (id: string) => void
  removeFromSelection: (folderIds: string[], diagramIds: string[]) => void
  clearSelection: () => void
}

type ModalMode = 'none' | 'rename' | 'share' | 'setIcon' | 'setCover'

type ContextTarget = {
  kind: 'folder' | 'diagram'
  id: string
  /**
   * Optional affordance guard supplied by a surface that already has the
   * projection metadata. The server remains the authority for enforcement.
   */
  protectedCanonicalOrgProjection?: boolean
} | null

type ModalState = {
  mode: ModalMode
  target: ContextTarget
  openRename: (target: ContextTarget) => void
  openShare: (target: ContextTarget) => void
  openSetIcon: (target: ContextTarget) => void
  openSetCover: (target: ContextTarget) => void
  close: () => void
}

export const useDashboardSelection = create<SelectionState>((set, get) => ({
  selectedFolderIds: [],
  selectedDiagramIds: [],
  toggleFolder: (id) => {
    const setIds = new Set(get().selectedFolderIds)
    if (setIds.has(id)) setIds.delete(id)
    else setIds.add(id)
    set({ selectedFolderIds: Array.from(setIds) })
  },
  toggleDiagram: (id) => {
    const setIds = new Set(get().selectedDiagramIds)
    if (setIds.has(id)) setIds.delete(id)
    else setIds.add(id)
    set({ selectedDiagramIds: Array.from(setIds) })
  },
  removeFromSelection: (folderIds, diagramIds) => {
    const folderSet = new Set(folderIds)
    const diagramSet = new Set(diagramIds)
    set((state) => ({
      selectedFolderIds: state.selectedFolderIds.filter((id) => !folderSet.has(id)),
      selectedDiagramIds: state.selectedDiagramIds.filter((id) => !diagramSet.has(id)),
    }))
  },
  clearSelection: () => set({ selectedFolderIds: [], selectedDiagramIds: [] })
}))

export const useDashboardModal = create<ModalState>((set) => ({
  mode: 'none',
  target: null,
  openRename: (target) => set({ mode: 'rename', target }),
  openShare: (target) => set({ mode: 'share', target }),
  openSetIcon: (target) => set({ mode: 'setIcon', target }),
  openSetCover: (target) => set({ mode: 'setCover', target }),
  close: () => set({ mode: 'none', target: null })
}))

type ContextMenuState = {
  open: boolean
  x: number
  y: number
  target: ContextTarget
  openAt: (x: number, y: number, target: ContextTarget) => void
  close: () => void
}

export const useDashboardContextMenu = create<ContextMenuState>((set) => ({
  open: false,
  x: 0,
  y: 0,
  target: null,
  openAt: (x, y, target) => set({ open: true, x, y, target }),
  close: () => set({ open: false })
}))
